import Delta from 'quill-delta';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Box } from 'reflexbox';

import { useLanguage } from '@treams/treams-hooks';
import {
	type LocalizedString,
	TranscriptionStatus,
	TranscriptionTargetType,
	TranscriptionUploadStatus,
	useFinalizeTranscriptionUploadMutation,
	useRegenerateTranscriptionSummaryMutation,
} from '@treams/treams-network';
import {
	Button,
	FlexGap,
	GraphQLErrorMessage,
	MicrophoneIcon,
	Paragraph,
	type TranscriptionSpeakerInfo,
	tokens,
} from '@treams/treams-ui';

import {
	useRecordingInAnotherTab,
	useStreamingTranscriptionContext,
} from './StreamingTranscriptionContext';
import { TranscriptionAttachment } from './TranscriptionAttachment';
import { localeToTranscriptionLanguage } from './TranscriptionLanguageSelect';
import { useTranscriptionRecordingContext } from './TranscriptionRecordingContext';
import { type StreamingSummaryActions, TranscriptionSummary } from './TranscriptionSummary';
import type { StreamingPhase, StreamingTarget } from './useStreamingTranscription';

const STREAMING_BUSY_PHASES: StreamingPhase[] = [
	'starting',
	'countdown',
	'recording',
	'pausing',
	'finishing',
	'cancelling',
];

export type TranscriptionFollowupGoalSuggestionData = {
	id: string;
	title: string;
	description: Delta;
	subgoals?: string[] | null;
	targetUser: { id: string; firstName: string };
	actedUpon: boolean;
};

export type TranscriptionFollowupFeedbackRequestSuggestionData = {
	id: string;
	title: string;
	instruction?: Delta | null;
	template?: { id: string; title: LocalizedString } | null;
	actedUpon: boolean;
};

export type TranscriptionFollowupsData = {
	goalSuggestions: TranscriptionFollowupGoalSuggestionData[];
	feedbackRequestSuggestions: TranscriptionFollowupFeedbackRequestSuggestionData[];
};

export type TranscriptionModuleData = {
	id: string;
	status: TranscriptionStatus;
	uploadStatus: TranscriptionUploadStatus;
	transcript?: string | null;
	summary?: string | null;
	editableSummary?: Delta;
	speakers?: TranscriptionSpeakerInfo[] | null;
	followups?: TranscriptionFollowupsData | null;
	audioUrl?: string | null;
	createdAt: string;
};

type Props = {
	targetID: string;
	targetType: TranscriptionTargetType;
	transcription?: TranscriptionModuleData | null;
	onLinkTranscription: (transcriptionID: string) => Promise<void>;
	onUnlinkTranscription: (transcriptionID?: string) => Promise<void>;
	unlinkLoading: boolean;
	onRefresh: () => void;
	canRemove: boolean;
	transcriptionLabel?: string;
	recordingLabel?: string;
	viewerCanRecord: boolean;
	/** Whether the viewer can edit the summary and act on its follow-ups. */
	viewerCanEditSummary?: boolean;
	disableRecording?: boolean;
	/**
	 * When set to 'inlineRecordButton', the module only renders a compact record
	 * button (without surrounding margins or helper text), and renders nothing
	 * when a transcription already exists or is recording. Useful for embedding
	 * the button inside another action row (e.g. a comment composer footer).
	 */
	variant?: 'default' | 'inlineRecordButton';
	/** SoundRecorderV2: record by streaming, unless the transcription has uploaded audio. */
	streamingEnabled?: boolean;
};

export const TranscriptionModule = ({
	targetID,
	targetType,
	transcription,
	onRefresh,
	onLinkTranscription,
	onUnlinkTranscription,
	unlinkLoading,
	canRemove,
	transcriptionLabel,
	recordingLabel,
	viewerCanRecord,
	viewerCanEditSummary = true,
	disableRecording,
	variant = 'default',
	streamingEnabled = false,
}: Props) => {
	const { t } = useTranslation();
	const location = useLocation();
	const { id: viewerLocale } = useLanguage();
	const transcriptionRecordingContext = useTranscriptionRecordingContext();
	const streamingContext = useStreamingTranscriptionContext();

	const [finalizeUpload, { loading: finalizeLoading, error: errorFinalize }] =
		useFinalizeTranscriptionUploadMutation();

	const hasTranscription = !!transcription;
	const hasUploadedAudio = transcription?.uploadStatus === TranscriptionUploadStatus.Uploaded;
	const usesStreaming = streamingEnabled && !hasUploadedAudio;

	const streamingPhase = streamingContext.isStreamingTarget(targetID)
		? streamingContext.state.phase
		: 'idle';
	const isStreamingThisTarget = STREAMING_BUSY_PHASES.includes(streamingPhase);
	const isStreamingBusy = STREAMING_BUSY_PHASES.includes(streamingContext.state.phase);
	const recordingInAnotherTab = useRecordingInAnotherTab(targetID, streamingEnabled);

	const isRecordingThisTarget =
		transcriptionRecordingContext.isRecordingTarget(targetID) || isStreamingThisTarget;
	const isContextBusy =
		!!transcriptionRecordingContext.activeSession || isStreamingBusy || recordingInAnotherTab;

	const { bindTarget } = streamingContext;
	const streamingTarget = useMemo<StreamingTarget>(
		() => ({
			targetID,
			targetType,
			label: recordingLabel,
			targetPath: location.pathname,
			onLinkTranscription,
			onUnlinkTranscription: (transcriptionID: string) => onUnlinkTranscription(transcriptionID),
			onRefresh,
		}),
		[
			targetID,
			targetType,
			recordingLabel,
			location.pathname,
			onLinkTranscription,
			onUnlinkTranscription,
			onRefresh,
		],
	);

	useEffect(() => {
		if (streamingEnabled) bindTarget(streamingTarget);
	}, [streamingEnabled, bindTarget, streamingTarget]);

	const isTranscriptionPending =
		hasTranscription &&
		(transcription.status === TranscriptionStatus.Pending ||
			transcription.status === TranscriptionStatus.Processing);

	const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	useEffect(() => {
		if (!isTranscriptionPending || isRecordingThisTarget) {
			if (pollIntervalRef.current) {
				clearInterval(pollIntervalRef.current);
				pollIntervalRef.current = null;
			}
			return;
		}

		pollIntervalRef.current = setInterval(() => {
			onRefresh();
		}, 5000);

		return () => {
			if (pollIntervalRef.current) {
				clearInterval(pollIntervalRef.current);
				pollIntervalRef.current = null;
			}
		};
	}, [isTranscriptionPending, onRefresh, isRecordingThisTarget]);

	const onRemoveTranscription = async () => {
		const removedTranscriptionId = transcription?.id;

		await onUnlinkTranscription();

		if (removedTranscriptionId) {
			transcriptionRecordingContext.resetInFlightRecordingForTranscription(removedTranscriptionId);
		}
		if (streamingContext.isStreamingTarget(targetID)) {
			streamingContext.close({ discardQueue: true });
		}
		onRefresh();
	};

	const onTranscribeAgain = async () => {
		if (!transcription) return;

		await finalizeUpload({
			variables: {
				input: { transcriptionID: transcription.id },
			},
		});

		onRefresh();
	};

	const [regenerateSummary] = useRegenerateTranscriptionSummaryMutation();

	const streamingLanguage =
		(streamingPhase !== 'idle' && streamingContext.state.language) ||
		localeToTranscriptionLanguage(viewerLocale);

	const handleContinue = () => {
		if (streamingPhase === 'paused' || streamingPhase === 'interrupted') {
			void streamingContext.resume();
			return;
		}

		void streamingContext.start(streamingTarget, {
			language: streamingLanguage,
			transcriptionID: transcription?.id ?? null,
		});
	};

	const handleFinish = () => {
		if (streamingPhase === 'paused' || streamingPhase === 'interrupted') {
			void streamingContext.finish();
			return;
		}

		if (!transcription) return;

		regenerateSummary({
			variables: { input: { transcriptionID: transcription.id } },
		})
			.then(() => onRefresh())
			.catch(() => {});
	};

	const isTranscriptionProcessing = transcription?.status === TranscriptionStatus.Processing;

	const streamingActions: StreamingSummaryActions | undefined = usesStreaming
		? {
				onContinue: transcription?.transcript ? handleContinue : undefined,
				continueDisabled: isContextBusy || isTranscriptionProcessing,
				onFinish: transcription?.transcript ? handleFinish : undefined,
			}
		: undefined;

	const handleStartRecording = () => {
		if (usesStreaming) {
			void streamingContext.start(streamingTarget, {
				language: localeToTranscriptionLanguage(viewerLocale),
				transcriptionID: transcription?.id ?? null,
			});
			return;
		}

		transcriptionRecordingContext.startRecording({
			targetID,
			targetType,
			label: recordingLabel,
			targetPath: location.pathname,
			onLinkTranscription,
			onUnlinkTranscription,
			onRefresh,
		});
	};

	const isInline = variant === 'inlineRecordButton';
	const recordButtonTitle = recordingLabel ?? t('transcription.recordConversation');
	const recordButtonIcon = <MicrophoneIcon color={tokens.error.icon} />;

	if (!viewerCanRecord && !hasTranscription) {
		if (isInline) {
			return (
				<Button title={recordButtonTitle} type="secondary" small disabled icon={recordButtonIcon} />
			);
		}
		return (
			<FlexGap mb="16px" alignItems="center" gap={8}>
				<Button title={recordButtonTitle} type="secondary" small disabled icon={recordButtonIcon} />
				{targetType === TranscriptionTargetType.EvaluationSubmission && (
					<Paragraph small>{t('transcription.onlyManagerCanRecord')}</Paragraph>
				)}
				{targetType === TranscriptionTargetType.Note && (
					<Paragraph small>{t('transcription.onlyNoteOwnerCanRecord')}</Paragraph>
				)}
			</FlexGap>
		);
	}

	if (disableRecording) {
		if (isInline) {
			return (
				<Button title={recordButtonTitle} type="secondary" small disabled icon={recordButtonIcon} />
			);
		}
		return (
			<FlexGap mb="16px" alignItems="center" gap={8}>
				<Button title={recordButtonTitle} type="secondary" small disabled icon={recordButtonIcon} />
				{targetType === TranscriptionTargetType.EvaluationSubmission && (
					<Paragraph small>{t('transcription.notAvailableUntilShared')}</Paragraph>
				)}
			</FlexGap>
		);
	}

	if (isInline) {
		if (hasTranscription || isRecordingThisTarget) {
			return null;
		}
		return (
			<Button
				title={recordButtonTitle}
				type="secondary"
				icon={recordButtonIcon}
				onClick={handleStartRecording}
				disabled={isContextBusy}
			/>
		);
	}

	return (
		<>
			{errorFinalize && (
				<Box mb="16px">
					<GraphQLErrorMessage error={errorFinalize.graphQLErrors[0]} />
				</Box>
			)}
			{!hasTranscription && !isRecordingThisTarget && viewerCanRecord && (
				<Box mb="24px">
					<FlexGap gap={8} alignItems="center">
						<Button
							title={recordButtonTitle}
							type="secondary"
							small
							icon={recordButtonIcon}
							onClick={handleStartRecording}
							disabled={isContextBusy}
						/>
						{isContextBusy && (
							<Paragraph small style={{ opacity: 0.7 }}>
								{recordingInAnotherTab
									? t('transcription.streaming.recordingInAnotherTab')
									: t('transcription.anotherRecordingInProgress')}
							</Paragraph>
						)}
					</FlexGap>
				</Box>
			)}
			{(hasTranscription || isStreamingThisTarget) && (
				<>
					<Box mb="16px">
						<TranscriptionAttachment
							transcription={transcription}
							onRemove={onRemoveTranscription}
							removing={unlinkLoading}
							canRemove={canRemove && !isStreamingThisTarget}
							label={transcriptionLabel}
							streamingState={streamingPhase !== 'idle' ? streamingContext.state : undefined}
						/>
					</Box>
					{recordingInAnotherTab && (
						<Box mb={2}>
							<Paragraph small style={{ opacity: 0.7 }}>
								{t('transcription.streaming.recordingInAnotherTab')}
							</Paragraph>
						</Box>
					)}
					{isTranscriptionPending && (
						<Box mb={2}>
							<Paragraph small style={{ opacity: 0.7 }}>
								{t('transcription.processingHint')}
							</Paragraph>
						</Box>
					)}
					{hasTranscription &&
						(transcription.status === TranscriptionStatus.Failed ? (
							<Box mb="24px">
								<FlexGap gap={12} flexDirection="column" alignItems="flex-start">
									<Paragraph small>{t('transcription.transcriptionFailed')}</Paragraph>
									{viewerCanRecord && !streamingActions && (
										<Button
											title={t('transcription.transcribeAgain')}
											type="ai"
											small
											loading={finalizeLoading}
											onClick={onTranscribeAgain}
										/>
									)}
								</FlexGap>
							</Box>
						) : (
							!isStreamingThisTarget && (
								<Box mb="24px">
									<TranscriptionSummary
										transcription={transcription}
										transcribeAgain={onTranscribeAgain}
										viewerCanRecord={viewerCanRecord}
										viewerCanEditSummary={viewerCanEditSummary}
										streamingActions={streamingActions}
									/>
								</Box>
							)
						))}
				</>
			)}
		</>
	);
};
